import { t } from "i18next";
import { useCallback, useState } from "react";

import Button from "~/components/buttons/Button";
import Hint from "~/components/Hint";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import { invoiceStatusId } from "~/modules/billing/api/invoiceStatus/invoiceStatusId.ts";
import { deleteQuota } from "~/modules/client/api/quota/quotaApiDispatchers.ts";
import type { QuotaWithRelations } from "~/modules/client/api/quota/quotaTypes.ts";


type Props = {
	onCancel: () => void;
	onSuccess: () => void;
	quota: QuotaWithRelations
};

const DeleteQuotaForm = ({
	onCancel,
	onSuccess,
	quota,
}: Props) => {
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");
	const [showConfirmation, setShowConfirmation] = useState<boolean>(false);

	const onSubmit = useCallback(async () => {
		try {
			setIsBusy(true);
			await deleteQuota(quota.clientId, quota.id);
			onSuccess();
		} catch (error) {
			console.log(error);
			setServerErrorMessage(t("form:unexpectedError"));
		}
	}, [onSuccess, quota.clientId, quota.id]);


	const quotaIsDeletable = quota.invoice.invoiceStatusId === invoiceStatusId.Pending;

	return (
		<div
			className="auto-grid-sidebar"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>Quota &quot;{quota.title}&quot; löschen</SidebarHeaderHeadline>
			</SidebarHeader>
			<SidebarContent>
				{isBusy && <SidebarBusyOverlay />}
				{!!serverErrorMessage &&
					<SidebarErrorOverlay title="Löschen fehlgeschlagen">{serverErrorMessage}</SidebarErrorOverlay>}
				{!quotaIsDeletable && <Hint theme="error"
											title="Löschen nicht möglich">
					Für dieses Abrechnungskontingent existiert bereits eine Rechnung, diese muss gelöscht oder storniert
					werden, bevor das Kontingent gelöscht werden kann.
				</Hint>}
				{quotaIsDeletable &&
					<Hint>Beim Löschen eines Abrechnungskontingent werden alle verknüpften Phase wieder freigegeben und die
						zugehörigen Erfassungen können bzw. müssen anderweitig abgerechnet werden. </Hint>}
				{quotaIsDeletable && showConfirmation &&
					<>
						<div className="h-4"/>
						<Hint theme="error">Bist du Dir sicher? Das Löschen kann nicht rückgängig gemacht werden!</Hint>
					</>}
			</SidebarContent>
			<SidebarFooter>
				{!showConfirmation && <Button theme="danger"
											  onClick={() => setShowConfirmation(true)}
											  disabled={!quotaIsDeletable}>
					Abrechnungskontingent löschen
				</Button>}


				<Button theme="white"
						onClick={onCancel}>
					{t("form:buttonTexts.cancel")}
				</Button>
				{showConfirmation && <Button theme="danger" onClick={onSubmit}>
					Löschen bestätigen
				</Button>}
			</SidebarFooter>
		</div>);
};

export default DeleteQuotaForm;
